// Formulario generado a partir del config de una sección + cálculo del PATCH.
// No hay esquema por componente en el backend: se infiere del contenido real
// (textos, títulos, botones/enlaces, imágenes por URL). Lo que no se reconoce
// queda en el editor JSON avanzado.

import type { Config, Json } from './api';

export type FieldKind = 'text' | 'textarea' | 'url' | 'image' | 'number' | 'bool';

const IMAGE_KEY = /(?:^|_)(?:image|img|src|logo|avatar|poster|photo|foto|background|bg|cover|thumbnail|icon_url)$/i;
const LINK_KEY = /(?:^|_)(?:href|url|link|to)$/i;
const LONG_KEY = /(?:^|_)(?:desc|description|subtitle|body|text|answer|a|content|paragraph|quote|bio|summary|lead|note|copy)$/i;

const LABELS: Record<string, string> = {
  title: 'Título', subtitle: 'Subtítulo', eyebrow: 'Antetítulo', kicker: 'Antetítulo', badge: 'Etiqueta',
  description: 'Descripción', desc: 'Descripción', body: 'Texto', text: 'Texto', content: 'Contenido',
  label: 'Texto del botón', href: 'Enlace', url: 'Enlace', link: 'Enlace', to: 'Enlace',
  image: 'Imagen', image_url: 'Imagen', background_image: 'Imagen de fondo', logo: 'Logo', avatar: 'Foto', poster: 'Portada del video', video: 'Video',
  cta_primary: 'Botón principal', cta_secondary: 'Botón secundario', cta: 'Botón', button: 'Botón', buttons: 'Botones',
  items: 'Elementos', features: 'Características', steps: 'Pasos', plans: 'Paquetes', testimonials: 'Testimonios',
  q: 'Pregunta', question: 'Pregunta', a: 'Respuesta', answer: 'Respuesta',
  name: 'Nombre', role: 'Cargo', quote: 'Cita', price: 'Precio', period: 'Periodo', icon: 'Ícono',
  placeholder: 'Texto de ayuda', nav_label: 'Nombre en el menú', headline: 'Titular', subheadline: 'Subtitular',
  highlight: 'Texto resaltado', note: 'Nota', stats: 'Cifras', value: 'Valor', caption: 'Pie de foto', alt: 'Texto alternativo',
};

const SECTION_LABELS: Record<string, string> = {
  hero: 'Portada', hero_immersive: 'Portada', hero_kinetic: 'Portada', about: 'Quiénes somos', features: 'Capacidades',
  signals: 'Capacidades', how_it_works: 'Cómo funciona', metodologia: 'Metodología', pricing: 'Paquetes',
  testimonials: 'Testimonios', faq: 'Preguntas frecuentes', cta: 'Invitación final', gallery: 'Galería', blog: 'Blog',
  newsletter: 'Suscripción', lead_form: 'Formulario', rich_text: 'Texto', logos_row: 'Franja de logos',
  integrations: 'Integraciones', trips: 'Catálogo de viajes', whatsapp_cta: 'Botón de WhatsApp', bento: 'Mosaico',
  product_catalog: 'Catálogo', ask_agent: 'Pregúntale al agente', stats: 'Cifras', team: 'Equipo', contact: 'Contacto',
  site_changelog: 'Cambios recientes', agent_console: 'Consola del agente', audience_router: 'Rutas por audiencia',
};

export function humanize(key: string): string {
  if (LABELS[key]) return LABELS[key];
  const s = key.replace(/[_-]+/g, ' ').trim();
  return s ? s[0].toUpperCase() + s.slice(1) : key;
}

export function sectionLabel(key: string): string {
  const [base, ...rest] = key.split('__');
  const name = SECTION_LABELS[base] ?? humanize(base);
  return rest.length ? `${name} · ${rest.join(' ').replace(/_/g, ' ')}` : name;
}

/** Título legible de un elemento de lista (para la cabecera plegable). */
export function itemTitle(v: Json, i: number): string {
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    for (const k of ['title', 'q', 'question', 'name', 'label', 'headline']) {
      const t = (v as Record<string, Json>)[k];
      if (typeof t === 'string' && t.trim()) return t.trim();
    }
  }
  if (typeof v === 'string' && v.trim()) return v.trim();
  return `Elemento ${i + 1}`;
}

export function kindOf(key: string, value: Json): FieldKind | null {
  if (typeof value === 'boolean') return 'bool';
  if (typeof value === 'number') return 'number';
  if (typeof value !== 'string') return null;
  if (IMAGE_KEY.test(key)) return 'image';
  if (LINK_KEY.test(key)) return 'url';
  if (LONG_KEY.test(key) || value.length > 90 || value.includes('\n')) return 'textarea';
  return 'text';
}

/** ¿El formulario sabe pintar este valor? (si no, solo el JSON avanzado) */
export function isFormable(value: Json, depth = 0): boolean {
  if (value === null) return false;
  if (typeof value !== 'object') return true;
  if (depth >= 3) return false;
  if (Array.isArray(value)) {
    if (value.length === 0) return false;
    const first = value[0];
    if (typeof first === 'string') return value.every((x) => typeof x === 'string');
    return value.every((x) => x && typeof x === 'object' && !Array.isArray(x)) && value.some((x) => isFormable(x, depth + 1));
  }
  return Object.values(value).some((v) => isFormable(v, depth + 1));
}

export function clone<T>(v: T): T {
  return v === undefined ? v : (JSON.parse(JSON.stringify(v)) as T);
}

export function equal(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function getAt(obj: Json, path: (string | number)[]): Json {
  let cur: Json = obj;
  for (const p of path) {
    if (cur === null || typeof cur !== 'object') return null;
    cur = (cur as any)[p] as Json;
    if (cur === undefined) return null;
  }
  return cur;
}

export function setAt(obj: Config, path: (string | number)[], value: Json | undefined): Config {
  const root = clone(obj);
  let cur: any = root;
  for (let i = 0; i < path.length - 1; i++) cur = cur[path[i]];
  const last = path[path.length - 1];
  if (value === undefined) {
    if (Array.isArray(cur)) cur.splice(Number(last), 1);
    else delete cur[last];
  } else cur[last] = value;
  return root;
}

/** Elemento nuevo para una lista: misma forma que el último, con textos vacíos. */
export function blankLike(v: Json): Json {
  if (typeof v === 'string') return '';
  if (typeof v === 'number') return 0;
  if (typeof v === 'boolean') return false;
  if (Array.isArray(v)) return [];
  if (v && typeof v === 'object') {
    const out: Record<string, Json> = {};
    for (const [k, x] of Object.entries(v)) out[k] = blankLike(x);
    return out;
  }
  return null;
}

const SEGMENT = /^[A-Za-z0-9_-]{1,64}$/;
const MAX_SET_PATHS = 50; // mismo tope que el backend (editing.MAX_SET_PATHS)

function isObj(v: unknown): v is Record<string, Json> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

/**
 * `set` del PATCH para llevar `before` (lo publicado) a `after` (lo editado):
 * rutas con puntos, null = quitar el campo, listas de largo distinto completas.
 * Si pasa del tope de rutas, cae a reemplazar las claves de primer nivel.
 */
export function setOps(before: Config, after: Config): Record<string, Json> {
  const out: Record<string, Json> = {};
  let fallback = false;
  const walk = (b: Json | undefined, a: Json | undefined, path: string[]) => {
    if (equal(b, a)) return;
    if (path.length && !SEGMENT.test(path[path.length - 1])) {
      fallback = true;
      return;
    }
    if (isObj(b) && isObj(a)) {
      for (const k of new Set([...Object.keys(b), ...Object.keys(a)])) walk(b[k], a[k], [...path, k]);
      return;
    }
    if (Array.isArray(b) && Array.isArray(a) && b.length === a.length) {
      a.forEach((x, i) => walk(b[i], x, [...path, String(i)]));
      return;
    }
    out[path.join('.')] = a === undefined ? null : a;
  };
  walk(before, after, []);
  if (fallback || Object.keys(out).length > MAX_SET_PATHS) {
    const top: Record<string, Json> = {};
    for (const k of new Set([...Object.keys(before), ...Object.keys(after)])) {
      if (!equal(before[k], after[k])) top[k] = after[k] === undefined ? null : after[k];
    }
    return top;
  }
  return out;
}
